import PhotoIcon from '@src/svgs/PhotoIcon';
import UploadIcon from '@src/svgs/UploadIcon';
import VideoIcon from '@src/svgs/VideoIcon';
import styled from 'styled-components';

import {
  getUploadTypeFromPhotoTab,
  useFileUpload,
} from '../../hooks/useFileUpload';
import { PhotoTab } from '../../types.ts/story';
import { trackDownload } from '../../utility/unsplash';
import SpinningLoading from '../SpinningLoading';
import { PrimaryButton, SecondaryButton } from './dialogStyles';
import { PICKER_MODAL_INSET } from './pickerModalStyles';
import Spinner from './Spinner';

import type { FileUploadAcceptType } from '../../hooks/useFileUpload';
import type { ImageKey, ImageWithType } from '../../types.ts/general';
import type { Artifact } from '../../types.ts/story';

type Props = {
  replaceAction: () => void;
  title: string;
  isSelected: boolean;
  selectedImage?: ImageWithType[ImageKey] | null;
  isLoading?: boolean;
  addImageLabel?: string;
  addImageLoadingLabel?: string;
  isAdding?: boolean;
  acceptFormats?: string[];
  tab: PhotoTab;
  setSelectedImage?: (img: ImageWithType[ImageKey] | null) => void;
  onHeadshotUpload?: (file: File) => Promise<void>;
  isUploadingHeadshot?: boolean;
  /**
   * An upload that is not a tab's library upload: the host labels it, picks
   * what it accepts, and takes the uploaded file (saved to the story's
   * library). Used by Replace Intro / End Card for a card made from the
   * creator's own image or video.
   */
  customUpload?: {
    label: string;
    acceptType: FileUploadAcceptType;
    onUploadComplete: (artifact: Artifact) => void;
  };
};

const PhotoModalTop = (props: Props) => {
  const isVideoTab =
    props.tab === PhotoTab.orgVideos || props.tab === PhotoTab.storyVideos;
  const { customUpload } = props;
  const { acceptFormats, handleUploadFile, isUploading } = useFileUpload({
    acceptType: customUpload?.acceptType ?? (isVideoTab ? 'video' : 'image'),
    uploadType: customUpload ? 'all' : getUploadTypeFromPhotoTab(props.tab),
    onUploadComplete: artifact => {
      if (customUpload) {
        if (artifact[0]) customUpload.onUploadComplete(artifact[0]);
        return;
      }
      const imageWithType: ImageWithType[ImageKey] = {
        id: artifact[0].id,
        url: artifact[0].url,
        type: 'artifact',
        width: artifact[0].width || '',
        height: artifact[0].height || '',
        alt: artifact[0].title || '',
      };
      props.setSelectedImage?.(imageWithType);
    },
  });

  const handleReplaceAction = () => {
    recordSelectedImageUsage();
    props.replaceAction();
  };

  const recordSelectedImageUsage = () => {
    if (props.selectedImage?.type === 'stock') {
      trackDownload(props.selectedImage.attribution?.download_location);
    }
  };

  const {
    addImageLabel = props.isAdding ? 'Add' : 'Replace',
    addImageLoadingLabel = props.isAdding ? 'Adding…' : 'Replacing…',
  } = props;

  const showUploadButton =
    props.tab === PhotoTab.artifact ||
    props.tab === PhotoTab.orgArtifact ||
    props.tab === PhotoTab.headshot ||
    isVideoTab ||
    !!customUpload;

  return (
    <>
      <Title>{props.title}</Title>
      {showUploadButton && (
        <ButtonContainer>
          <UploadButton
            onClick={() => window.getSelection()?.empty()}
            disabled={
              props.tab === PhotoTab.headshot
                ? props.isUploadingHeadshot
                : isUploading
            }
          >
            {customUpload ? (
              <UploadIcon width="16" height="16" />
            ) : isVideoTab ? (
              <VideoIcon
                width="16"
                height="16"
                viewBox="0 0 14 14"
                strokeColor="currentColor"
              />
            ) : (
              <PhotoIcon
                width="16"
                height="16"
                strokeColor="currentColor"
                strokeWidth={1.5}
              />
            )}
            {customUpload
              ? customUpload.label
              : props.tab === PhotoTab.headshot
                ? 'Upload Headshot'
                : isVideoTab
                  ? 'Upload Video'
                  : 'Upload Image'}
            <FileInput
              key={
                (
                  props.tab === PhotoTab.headshot
                    ? props.isUploadingHeadshot
                    : isUploading
                )
                  ? 'upload-disabled'
                  : 'upload-enabled'
              }
              type="file"
              accept={
                props.tab === PhotoTab.headshot
                  ? 'image/png,image/jpeg,image/jpg,image/webp'
                  : acceptFormats?.join(',') || 'image/*'
              }
              onChange={
                props.tab === PhotoTab.headshot
                  ? e => {
                      const file = e.target.files?.[0];
                      if (file) props.onHeadshotUpload?.(file);
                    }
                  : handleUploadFile
              }
              disabled={
                props.tab === PhotoTab.headshot
                  ? props.isUploadingHeadshot
                  : isUploading
              }
              title=""
            />
          </UploadButton>
        </ButtonContainer>
      )}
      {/* Rendered here but displayed at the bottom of the modal: the hosts'
          modal bodies are flex columns, and this bar's `order: 1` sorts it
          after the content (matching the bottom-centered primary-action
          standard, e.g. Create New Speaker). In a non-flex host it simply
          stays here in the flow. */}
      <FooterBar>
        <ReplaceButton
          onClick={handleReplaceAction}
          disabled={!props.isSelected || props.isLoading}
          aria-busy={props.isLoading || undefined}
        >
          {props.isLoading && <Spinner size="sm" />}
          {props.isLoading ? addImageLoadingLabel : addImageLabel}
        </ReplaceButton>
      </FooterBar>
      {(isUploading || props.isUploadingHeadshot) && (
        <SpinningLoading
          customStyle={{ top: 0, zIndex: 1 }}
          Ico={
            customUpload ? (
              <UploadIcon width="24" height="24" />
            ) : isVideoTab ? (
              <VideoIcon strokeColor="var(--primary)" />
            ) : (
              <PhotoIcon strokeColor="var(--primary)" strokeWidth={2} />
            )
          }
          text={
            props.isUploadingHeadshot
              ? 'Uploading headshot…'
              : customUpload
                ? 'Uploading…'
                : isVideoTab
                  ? 'Uploading video…'
                  : 'Uploading image…'
          }
        />
      )}
    </>
  );
};

export default PhotoModalTop;

/* Left edge sits on the shared picker inset (tabs, search, cards, footer);
   the right padding clears the shared Modal's corner close button. */
const Title = styled.h3`
  margin: 0 0 var(--spacing-md);
  padding: 0 48px 0 ${PICKER_MODAL_INSET};
  color: var(--foreground);
  font-size: var(--text-md);
  font-weight: var(--font-weight-semi-bold);
  line-height: var(--leading-text-md);
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  padding: 0 ${PICKER_MODAL_INSET};
  margin-bottom: var(--spacing-md);
`;

/* `order: 1` places the bar visually last inside a flex-column modal body —
   see the comment at the render site. */
const FooterBar = styled.div`
  order: 1;
  display: flex;
  justify-content: center;
  padding: var(--spacing-md) ${PICKER_MODAL_INSET} 0;
`;

const ReplaceButton = styled(PrimaryButton)`
  min-width: 132px;
`;

const UploadButton = styled(SecondaryButton)`
  position: relative;
`;

const FileInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  &:disabled {
    display: none;
  }
`;
